import type { OrderResponse, OrderStatus } from '@/services/ordersApi'
import { Button } from '@/components/ui/button'
import {
  AlertCircle,
  Ban,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  Receipt,
  ShoppingCart,
  MessageCircle,
} from 'lucide-react'
import { buildOrderReceiptUrl, formatOrderMoney, getOrderStatusBadge, isOrderEditable } from './ordersUx'

export interface OrderEmptyState {
  title: string
  description: string
  primaryAction: string
  secondaryAction: string
}

interface OrdersTableProps {
  orders: readonly OrderResponse[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  emptyState: OrderEmptyState | null
  hasActiveFilters: boolean
  primaryActionFor: (status: OrderStatus) => { label: string; next: OrderStatus } | null
  onOpen: (order: OrderResponse) => void
  onEdit: (order: OrderResponse) => void
  onNotify: (order: OrderResponse) => void
  onCancel: (order: OrderResponse) => void
  onAdvance: (order: OrderResponse, next: OrderStatus) => void
  updatingOrderId: string | null
  onClearFilters: () => void
  onGoConversations: () => void
  onGoProducts: () => void
  onCreate: () => void
  page: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
}

const TONE_CLASS: Record<string, string> = {
  amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  rose: 'bg-rose-100 text-rose-800',
  slate: 'bg-slate-100 text-slate-700',
}

function deliveryLabel(method: string | null): string {
  if (!method) return 'Sin definir'
  const normalized = method.toLowerCase()
  if (['pickup', 'store_pickup', 'retiro'].includes(normalized)) return 'Retiro'
  if (['delivery', 'cash_on_delivery', 'shipping'].includes(normalized)) return 'Delivery'
  return method
}

function OrdersSkeleton() {
  return (
    <div className="space-y-2 p-3" aria-label="Cargando órdenes" role="status">
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="h-14 animate-pulse rounded-xl bg-slate-100" />
      ))}
    </div>
  )
}

function OrdersErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
      <AlertCircle className="mx-auto h-10 w-10 text-rose-400" />
      <p className="mt-3 text-sm font-semibold text-rose-900">No pudimos cargar las órdenes</p>
      <p className="mt-1 text-sm text-rose-700">Revisa la conexión o vuelve a intentar. Los datos siguen intactos.</p>
      <Button type="button" variant="outline" className="mt-4" onClick={onRetry}>
        Reintentar
      </Button>
    </div>
  )
}

function OrdersEmpty({
  emptyState,
  hasActiveFilters,
  onClearFilters,
  onGoConversations,
  onGoProducts,
  onCreate,
}: Pick<
  OrdersTableProps,
  'emptyState' | 'hasActiveFilters' | 'onClearFilters' | 'onGoConversations' | 'onGoProducts' | 'onCreate'
>) {
  if (!emptyState) return null
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
      <ShoppingCart className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm font-semibold text-slate-800">{emptyState.title}</p>
      <p className="mt-1 text-sm text-slate-600">{emptyState.description}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {hasActiveFilters ? (
          <Button type="button" variant="outline" onClick={onClearFilters}>
            {emptyState.primaryAction}
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={onGoConversations}>
              {emptyState.primaryAction}
            </Button>
            <Button type="button" variant="outline" onClick={onGoProducts}>
              {emptyState.secondaryAction}
            </Button>
            <Button type="button" onClick={onCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Crear manualmente
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default function OrdersTable(props: OrdersTableProps) {
  const {
    orders,
    isLoading,
    isError,
    onRetry,
    emptyState,
    hasActiveFilters,
    primaryActionFor,
    onOpen,
    onEdit,
    onNotify,
    onCancel,
    onAdvance,
    updatingOrderId,
    onClearFilters,
    onGoConversations,
    onGoProducts,
    onCreate,
    page,
    totalPages,
    total,
    onPageChange,
  } = props

  if (isError) return <OrdersErrorState onRetry={onRetry} />
  if (isLoading && orders.length === 0) return <OrdersSkeleton />
  if (orders.length === 0) {
    return (
      <OrdersEmpty
        emptyState={emptyState}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        onGoConversations={onGoConversations}
        onGoProducts={onGoProducts}
        onCreate={onCreate}
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="hidden min-h-0 flex-1 overflow-auto md:block">
        <table className="w-full min-w-[860px] border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Orden</th>
              <th scope="col" className="px-4 py-3 font-semibold">Cliente</th>
              <th scope="col" className="px-4 py-3 font-semibold">Producto(s)</th>
              <th scope="col" className="px-4 py-3 font-semibold">Entrega</th>
              <th scope="col" className="px-4 py-3 text-right font-semibold">Total</th>
              <th scope="col" className="px-4 py-3 font-semibold">Estado</th>
              <th scope="col" className="px-4 py-3 text-right font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((order) => {
              const badge = getOrderStatusBadge(order.status)
              const action = primaryActionFor(order.status)
              const updating = updatingOrderId === order.id
              const firstItem = order.items[0]
              return (
                <tr
                  key={order.id}
                  className="group cursor-pointer transition-colors hover:bg-slate-50"
                  onClick={() => onOpen(order)}
                >
                  <td className="px-4 py-3 align-top">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpen(order)
                      }}
                      className="text-left font-mono text-xs font-semibold text-slate-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                      aria-label={`Abrir orden ${order.orderNumber}`}
                    >
                      {order.orderNumber}
                    </button>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {new Date(order.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'short' })}
                    </p>
                  </td>
                  <td className="max-w-[180px] px-4 py-3 align-top">
                    <p className="truncate font-semibold text-slate-900">
                      {order.customerName || order.customerPhone || 'Sin cliente'}
                    </p>
                    {order.customerPhone && order.customerName ? (
                      <p className="truncate text-[11px] text-slate-500">{order.customerPhone}</p>
                    ) : null}
                  </td>
                  <td className="max-w-[230px] px-4 py-3 align-top">
                    <p className="truncate text-slate-800">
                      {firstItem ? `${firstItem.quantity}× ${firstItem.name}` : 'Sin productos'}
                    </p>
                    {order.items.length > 1 ? (
                      <p className="text-[11px] text-slate-500">+{order.items.length - 1} más</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top text-slate-700">
                    <span className="inline-flex items-center gap-1">
                      <Package className="h-3.5 w-3.5 text-slate-400" />
                      {deliveryLabel(order.fulfillmentMethod)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right align-top font-semibold text-slate-900">
                    {formatOrderMoney(order.totalCents, order.currency)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={[
                        'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
                        TONE_CLASS[badge.tone] ?? TONE_CLASS.slate,
                      ].join(' ')}
                    >
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center justify-end gap-1.5" onClick={(event) => event.stopPropagation()}>
                      {action ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={updating}
                          onClick={() => onAdvance(order, action.next)}
                          className="min-h-[36px] whitespace-nowrap bg-brand text-white hover:bg-brand-dark"
                        >
                          {updating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                          {action.label}
                        </Button>
                      ) : null}
                      <details className="relative">
                        <summary
                          className="inline-flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 [&::-webkit-details-marker]:hidden"
                          aria-label={`Más acciones para ${order.orderNumber}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </summary>
                        <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                          {isOrderEditable(order.status) ? (
                            <button type="button" onClick={() => onEdit(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                              <Pencil className="h-4 w-4 text-slate-400" /> Editar
                            </button>
                          ) : null}
                          <button type="button" onClick={() => onNotify(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                            <MessageCircle className="h-4 w-4 text-slate-400" /> Notificar
                          </button>
                          <button
                            type="button"
                            onClick={() => window.open(buildOrderReceiptUrl(order.id), '_blank', 'noopener,noreferrer')}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                          >
                            <Receipt className="h-4 w-4 text-slate-400" /> Recibo
                          </button>
                          {order.status !== 'cancelled' && order.status !== 'completed' ? (
                            <button type="button" onClick={() => onCancel(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50">
                              <Ban className="h-4 w-4" /> Cancelar
                            </button>
                          ) : null}
                        </div>
                      </details>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 p-3 md:hidden">
        {orders.map((order) => {
          const badge = getOrderStatusBadge(order.status)
          const action = primaryActionFor(order.status)
          const updating = updatingOrderId === order.id
          return (
            <div key={order.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <button type="button" onClick={() => onOpen(order)} className="w-full text-left">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] text-slate-500">{order.orderNumber}</p>
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {order.customerName || order.customerPhone || 'Sin cliente'}
                    </p>
                  </div>
                  <span className={['shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold', TONE_CLASS[badge.tone] ?? TONE_CLASS.slate].join(' ')}>
                    {badge.label}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-600">
                  {order.items[0] ? `${order.items[0].quantity}× ${order.items[0].name}` : 'Sin productos'} · {deliveryLabel(order.fulfillmentMethod)}
                </p>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{new Date(order.createdAt).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="font-semibold text-slate-900">{formatOrderMoney(order.totalCents, order.currency)}</span>
                </div>
              </button>
              <div className="mt-3 flex items-center gap-2">
                {action ? (
                  <Button type="button" size="sm" disabled={updating} onClick={() => onAdvance(order, action.next)} className="min-h-[44px] flex-1 bg-brand text-white hover:bg-brand-dark">
                    {updating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                    {action.label}
                  </Button>
                ) : null}
                <Button type="button" size="sm" variant="outline" onClick={() => onOpen(order)} className="min-h-[44px]">
                  Ver detalle
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-2 text-xs text-slate-500">
        <span>
          {orders.length} en esta página · {total} en total
        </span>
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Página anterior">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-2 tabular-nums">
            {page} / {Math.max(1, totalPages)}
          </span>
          <Button type="button" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Página siguiente">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
